import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Briefcase, Phone, MessageCircle, Mail, ArrowLeft, CheckCircle2, Megaphone, BadgeCheck } from "lucide-react";
import { Navbar, Footer } from "@/components/Navbar";
import { ContactButton } from "@/components/ContactButton";
import api from "@/lib/api";

export default function ProfileDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [ads, setAds] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/professionals/${id}`).then((r) => setP(r.data)).catch(() => setNotFound(true));
    api.get("/propagandas").then((r) => setAds(r.data.ads || [])).catch(() => {});
  }, [id]);

  if (notFound) return (
    <div className="min-h-screen bg-[#fafaf8]"><Navbar /><div className="max-w-3xl mx-auto px-4 py-24 text-center text-[#78716c]">Profissional não encontrado.</div></div>
  );
  if (!p) return <div className="min-h-screen bg-[#fafaf8]"><Navbar /><div className="max-w-3xl mx-auto px-4 py-24 text-center text-[#78716c]">Carregando...</div></div>;

  return (
    <div className="min-h-screen bg-[#fafaf8]">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/diretorio" className="inline-flex items-center gap-2 text-sm text-[#78716c] hover:text-[#1b5e3f] mb-6"><ArrowLeft className="w-4 h-4" /> Voltar ao diretório</Link>

        <div className="bg-white rounded-2xl border border-[#e7e5e4] shadow-sm overflow-hidden">
          <div className="h-32 bg-[#1b5e3f] relative">
            <div className="absolute -bottom-12 left-8 w-24 h-24 rounded-2xl bg-white border-4 border-white overflow-hidden shadow-md flex items-center justify-center">
              {p.photo_url ? <img src={p.photo_url} alt={p.name} className="w-full h-full object-cover" /> : <span className="font-head text-4xl font-bold text-[#1b5e3f]">{(p.name || "?")[0]}</span>}
            </div>
          </div>
          <div className="pt-16 px-8 pb-8">
            <h1 className="font-head text-3xl font-bold text-[#1c1917] flex items-center gap-2" data-testid="profile-name">
              {p.name}
              {p.verified && <BadgeCheck data-testid="verified-tick" className="w-6 h-6 text-[#2563eb] shrink-0" title="Perfil verificado" />}
            </h1>
            {p.verified && <p className="text-sm text-[#2563eb] font-medium inline-flex items-center gap-1 mt-1"><BadgeCheck className="w-4 h-4" /> Perfil verificado pelo portal</p>}
            <div className="flex flex-wrap items-center gap-3 mt-3">
              <span className="bg-[#e8f0ec] text-[#1b5e3f] px-3 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1"><Briefcase className="w-3 h-3" /> {p.sector}</span>
              <span className="bg-[#fef3c7] text-[#d97706] px-3 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {p.city}, {p.region}</span>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mt-8">
              <div className="md:col-span-2 space-y-6">
                <div>
                  <h2 className="font-head text-lg font-semibold text-[#1c1917]">Sobre</h2>
                  <p className="text-[#78716c] mt-2 leading-relaxed whitespace-pre-line">{p.bio || "Sem descrição."}</p>
                </div>
                {p.services?.length > 0 && (
                  <div>
                    <h2 className="font-head text-lg font-semibold text-[#1c1917]">Serviços oferecidos</h2>
                    <ul className="mt-3 space-y-2">
                      {p.services.map((s, i) => <li key={i} className="flex items-center gap-2 text-[#1c1917]"><CheckCircle2 className="w-4 h-4 text-[#1b5e3f]" /> {s}</li>)}
                    </ul>
                  </div>
                )}
              </div>

              <div className="bg-[#fafaf8] rounded-2xl border border-[#e7e5e4] p-5 h-fit">
                <h3 className="font-head font-semibold text-[#1c1917] mb-3">Contato</h3>
                <div className="mb-4"><ContactButton contextType="professional" contextId={p.id} /></div>
                <div className="space-y-3">
                  {p.whatsapp && <a data-testid="profile-whatsapp" href={`https://wa.me/${p.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-[#1c1917] hover:text-[#1b5e3f]"><MessageCircle className="w-4 h-4 text-[#1b5e3f]" /> {p.whatsapp}</a>}
                  {p.phone && <div className="flex items-center gap-2 text-sm text-[#1c1917]"><Phone className="w-4 h-4 text-[#1b5e3f]" /> {p.phone}</div>}
                  {p.contact_email && <a href={`mailto:${p.contact_email}`} className="flex items-center gap-2 text-sm text-[#1c1917] hover:text-[#1b5e3f] break-all"><Mail className="w-4 h-4 text-[#1b5e3f]" /> {p.contact_email}</a>}
                  {!p.whatsapp && !p.phone && !p.contact_email && <p className="text-sm text-[#78716c]">Sem contato disponível.</p>}
                </div>
              </div>

              {ads.length > 0 && (
                <div className="mt-6" data-testid="profile-propagandas">
                  <p className="text-xs uppercase tracking-[0.15em] font-bold text-[#78716c] mb-3 flex items-center gap-1"><Megaphone className="w-3.5 h-3.5" /> Propagandas em destaque</p>
                  <div className="space-y-3">
                    {ads.slice(0, 3).map((ad) => (
                      <a key={ad.id} href={ad.link_url || "#"} target={ad.link_url ? "_blank" : undefined} rel="noreferrer" data-testid={`profile-ad-${ad.id}`} className={`block rounded-2xl border overflow-hidden hover:shadow-md transition-all ${ad.official ? "border-[#1b5e3f]/30 ring-1 ring-[#1b5e3f]/10 bg-white" : "border-[#e7e5e4] bg-white"}`}>
                        {ad.image_url ? (
                          <img src={ad.image_url} alt={ad.title} className="w-full h-24 object-cover" />
                        ) : (
                          <div className="w-full h-20 bg-[#e8f0ec] flex items-center justify-center"><Megaphone className="w-6 h-6 text-[#1b5e3f]/40" /></div>
                        )}
                        <div className="p-3">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-[10px] uppercase font-bold text-[#d97706]">Anúncio</span>
                            {ad.official && <span className="text-[10px] uppercase font-bold text-[#1b5e3f]">Oficial</span>}
                          </div>
                          <p className="font-head font-semibold text-sm text-[#1c1917] mt-0.5">{ad.title}</p>
                          <p className="text-xs text-[#78716c] line-clamp-2">{ad.description}</p>
                        </div>
                      </a>
                    ))}
                  </div>
                  <Link to="/propagandas" className="text-xs font-semibold text-[#1b5e3f] hover:underline mt-3 inline-block">Ver todas as propagandas →</Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
